import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box, Grid, Typography, Paper, Avatar, Button, Chip, Divider,
  Radio, RadioGroup, FormControlLabel, Checkbox, Select, MenuItem, FormControl,
  CircularProgress, IconButton, Collapse, Slider, Drawer, useMediaQuery, useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import SortOutlinedIcon from '@mui/icons-material/SortOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { searchAll } from '../../actions/profile';
import { getCategories, getSubcategories } from '../../actions/categories';
import { urlImg, isEmpty } from '../../components/common/helpers';
import { conQuienImparte } from '../../components/common/imparte';
import SearchMap from '../../components/search/SearchMap';
import { dinero } from '../../utils/dinero';

const resolveUserAvatar = (avatar, userId, version) => {
  if (isEmpty(avatar)) return '';
  const a = avatar.toString();
  if (a.startsWith('http')) return a;
  if (a.startsWith('//')) return a.replace(/^\/\//, 'https://');
  const v = version ? `v${version}/` : '';
  return `${urlImg}/w_150,h_150,c_fill/${v}users/${userId}/${a}`;
};

// Precio de un servicio: llega como texto (así lo guarda el modelo); vacío o algo que no es número cuenta como 0
const precioDe = (servicio) => {
  const n = Number(servicio.costoServicio);
  return Number.isFinite(n) ? n : 0;
};

const RatingChip = ({ rating }) => (
  rating > 0 ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
      <StarIcon sx={{ fontSize: 16, color: 'primary.main' }} />
      <Typography variant="body2" fontWeight={700} color="primary.main">{rating.toFixed(1)}</Typography>
    </Box>
  ) : (
    <Typography variant="caption" fontWeight={700} color="text.secondary">Nuevo</Typography>
  )
);

const PersonaCard = ({ persona, onVer }) => {
  const avatar = resolveUserAvatar(persona.avatar, persona.userId, persona.versionAvatar);
  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '16px', border: 1, borderColor: 'sutil', display: 'flex', gap: 2, alignItems: 'center' }}>
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <Avatar src={avatar} sx={{ width: 90, height: 90, borderRadius: '12px' }} variant="rounded">
          {persona.name?.charAt(0)}
        </Avatar>
        {persona.identidadVerificada && (
          <Chip icon={<VerifiedUserIcon sx={{ fontSize: 12, color: 'white !important' }} />} label="Verificado"
            sx={{ position: 'absolute', top: 6, left: 6, height: 18, bgcolor: (t) => alpha(t.palette.primary.main, 0.75), color: 'white', fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }} />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={700} color="primary.main" noWrap>
            {persona.name} {persona.apellido}
          </Typography>
          <RatingChip rating={persona.rating} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{persona.rol}</Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocalShippingOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography variant="caption" color="text.secondary">{persona.serviceCount} servicios</Typography>
          </Box>
          {/* Cuántas opiniones tiene de verdad, como las tarjetas del perfil y la ficha; sin opiniones no se dice nada */}
          <Typography variant="caption" color="text.secondary">
            {persona.ratingTotal > 0 && `${persona.ratingTotal} ${persona.ratingTotal === 1 ? 'opinión' : 'opiniones'}`}
          </Typography>
        </Box>
      </Box>
      <Button variant="contained" onClick={() => onVer(persona)} disableElevation
        sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: '8px', px: 3, flexShrink: 0, '&:hover': { bgcolor: 'primary.dark' } }}>
        Ver perfil
      </Button>
    </Paper>
  );
};

// Muestra a quien imparte el servicio (por omisión, quien lo creó); `onVer` recibe el servicio tal cual, con su proveedor
const ServicioCard = ({ servicio: original, onVer: verOriginal }) => {
  const servicio = conQuienImparte(original);
  const onVer = () => verOriginal(original);
  const img = servicio.img ? `${urlImg}/w_400,h_250,c_fill/servicios/${servicio._id}/${servicio.img}` : '/search.png';
  const avatar = resolveUserAvatar(servicio.authorAvatar, servicio.autorId, servicio.authorAvatarVersion);
  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '16px', border: 1, borderColor: 'sutil', display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
      <Box sx={{ position: 'relative', width: { xs: '100%', sm: 180 }, height: 130, borderRadius: '12px', overflow: 'hidden', flexShrink: 0 }}>
        <Box component="img" src={img} alt={servicio.nombre} onClick={() => onVer(servicio)} sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }} />
        <Chip label={servicio.categoriaNombre} size="small"
          sx={{ position: 'absolute', top: 8, left: 8, height: 20, bgcolor: (t) => alpha(t.palette.primary.main, 0.75), color: 'white', fontSize: '0.65rem' }} />
        <Box sx={{ position: 'absolute', bottom: 6, left: 6, display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: '20px', px: 0.8, py: 0.2 }}>
          <Avatar src={avatar} sx={{ width: 18, height: 18 }} />
          <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ fontSize: '0.6rem' }} noWrap>{servicio.author}</Typography>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={700} color="primary.main" noWrap>{servicio.nombre}</Typography>
          <RatingChip rating={servicio.rating} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>{servicio.author}</Typography>
        {servicio.descripcion && (
          <Typography variant="body2" color="text.secondary" sx={{
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word', mb: 1
          }}>
            {servicio.descripcion}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
          {servicio.ubicacion && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <LocationOnIcon sx={{ fontSize: 15, color: 'primary.main' }} />
              <Typography variant="caption" color="text.secondary" noWrap>{servicio.ubicacion}</Typography>
            </Box>
          )}
          {servicio.duracionEstimada && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <AccessTimeIcon sx={{ fontSize: 15, color: 'primary.main' }} />
              <Typography variant="caption" color="text.secondary">{servicio.duracionEstimada} {servicio.unidadDuracion}</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box>
            <Typography variant="h6" fontWeight={800} color="primary.main" sx={{ lineHeight: 1 }}>
              {dinero(servicio.costoServicio)}
            </Typography>
            <Typography variant="caption" color="text.secondary">por {servicio.tipoCosto?.toLowerCase()}</Typography>
          </Box>
          <Button variant="contained" onClick={() => onVer(servicio)} disableElevation
            sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: '8px', '&:hover': { bgcolor: 'primary.dark' } }}>
            Ver detalle
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

const PaginaCard = ({ pagina, onVer }) => {
  const img = pagina.img ? `${urlImg}/w_400,h_250,c_fill/paginas/${pagina._id}/${pagina.img}` : '/search.png';
  const avatar = resolveUserAvatar(pagina.authorAvatar, pagina.userId, pagina.authorAvatarVersion);
  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '16px', border: 1, borderColor: 'sutil', display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
      <Box sx={{ position: 'relative', width: { xs: '100%', sm: 180 }, height: 130, borderRadius: '12px', overflow: 'hidden', flexShrink: 0 }}>
        <Box component="img" src={img} alt={pagina.nombre} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <Chip label={pagina.categoriaNombre} size="small"
          sx={{ position: 'absolute', top: 8, left: 8, height: 20, bgcolor: (t) => alpha(t.palette.primary.main, 0.75), color: 'white', fontSize: '0.65rem' }} />
        <Box sx={{ position: 'absolute', bottom: 6, left: 6, display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: '20px', px: 0.8, py: 0.2 }}>
          <Avatar src={avatar} sx={{ width: 18, height: 18 }} />
          <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ fontSize: '0.6rem' }} noWrap>{pagina.author}</Typography>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={700} color="primary.main" noWrap>{pagina.nombre}</Typography>
          <RatingChip rating={pagina.rating} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mb: 1
        }}>
          {pagina.descripcion}
        </Typography>
        <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box>
            <Typography variant="h6" fontWeight={800} color="primary.main" sx={{ lineHeight: 1 }}>{pagina.serviciosDisponibles}</Typography>
            <Typography variant="caption" color="text.secondary">Servicios disponibles</Typography>
          </Box>
          <Button variant="contained" onClick={() => onVer(pagina)} disableElevation
            sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: '8px', '&:hover': { bgcolor: 'primary.dark' } }}>
            Ver página
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

const Section = ({ title, subtitle, count, itemHeight = 170, children }) => {
  // En pantallas grandes se ven ~5 tarjetas; si hay más, la lista tiene su scroll (la 6ª asoma para indicar que hay más).
  // En el celular va completa: un scroll dentro de otro hacía que se moviera la lista y no la página.
  const scroll = count > 5;
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="primary.main">{title}</Typography>
          <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
        </Box>
        {count > 0 && <Typography variant="body2" fontWeight={700} color="secondary.main">{count} resultado{count !== 1 ? 's' : ''}</Typography>}
      </Box>
      <Box sx={scroll ? { maxHeight: { md: itemHeight * 5.4 }, overflowY: { md: 'auto' }, pr: { md: 1 } } : {}}>
        {children}
      </Box>
    </Box>
  );
};

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const categories = useSelector(state => state.cat.categories);

  const q = searchParams.get('q') || '';
  const categoria = searchParams.get('categoria') || '';
  const subcategoria = searchParams.get('subcategoria') || '';
  const tag = searchParams.get('tag') || '';

  const [data, setData] = useState({ personas: [], servicios: [], paginasIndependientes: [], paginasGrupos: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [subcategories, setSubcategories] = useState([]);

  // Filtros (cliente)
  const [orden, setOrden] = useState('rating');
  const [tipos, setTipos] = useState({ personas: true, servicios: true, independientes: true, grupos: true });
  // Rango de precio: null (sin filtro) hasta que la persona mueve el control. Antes arrancaba en $0–$10,000 y escondía siempre lo más caro
  const [precio, setPrecio] = useState(null);
  const [advOpen, setAdvOpen] = useState(false);
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false); // la hoja de filtros del celular
  const [mapaAbierto, setMapaAbierto] = useState(false); // el mapa del celular
  // En pantallas grandes el mapa se ve siempre; en el celular, solo si la persona lo pide (si no, ni se arma ni se buscan las direcciones)
  const theme = useTheme();
  const pantallaGrande = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    if (!categories || categories.length === 0) dispatch(getCategories());
  }, [dispatch]);

  useEffect(() => {
    if (categoria) getSubcategories(categoria).then(d => setSubcategories(d || []));
    else setSubcategories([]);
  }, [categoria]);

  useEffect(() => {
    setLoading(true);
    setPrecio(null); // Con otros resultados el rango vuelve a empezar: su tope depende de ellos
    searchAll({ q, categoria, subcategoria, tag }).then(d => {
      setData(d);
      setLoading(false);
    });
  }, [q, categoria, subcategoria, tag]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    if (key === 'categoria') next.delete('subcategoria');
    setSearchParams(next);
  };

  // Tope del control de precio: el precio más alto de los resultados, redondeado hacia arriba a su paso
  const { paso, tope } = useMemo(() => {
    const maximo = data.servicios.reduce((m, s) => Math.max(m, precioDe(s)), 0);
    const p = maximo <= 1000 ? 10 : 100;
    return { paso: p, tope: Math.max(p, Math.ceil(maximo / p) * p) };
  }, [data]);
  const rango = precio || [0, tope];

  // Aplicar filtros cliente (precio, solo si la persona lo movió) y orden
  const procesar = (lista, tienePrecio) => {
    let res = [...lista];
    if (tienePrecio && precio) res = res.filter(x => precioDe(x) >= precio[0] && precioDe(x) <= precio[1]);
    if (tienePrecio && orden === 'priceAsc') res.sort((a, b) => a.costoServicio - b.costoServicio);
    else if (tienePrecio && orden === 'priceDesc') res.sort((a, b) => b.costoServicio - a.costoServicio);
    else res.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    return res;
  };

  const personas = useMemo(() => procesar(data.personas, false), [data, orden]);
  const servicios = useMemo(() => procesar(data.servicios, true), [data, orden, precio]);
  const independientes = useMemo(() => procesar(data.paginasIndependientes, false), [data, orden]);
  const grupos = useMemo(() => procesar(data.paginasGrupos, false), [data, orden]);

  const totalVisible =
    (tipos.personas ? personas.length : 0) +
    (tipos.servicios ? servicios.length : 0) +
    (tipos.independientes ? independientes.length : 0) +
    (tipos.grupos ? grupos.length : 0);

  // Filtros que cambió la persona: el número del botón «Filtros» en el celular (como en la app)
  const filtrosActivos = [orden !== 'rating', !!categoria, Object.values(tipos).some(v => !v), !!precio].filter(Boolean).length;
  const limpiarFiltros = () => {
    setOrden('rating');
    setTipos({ personas: true, servicios: true, independientes: true, grupos: true });
    setPrecio(null);
    if (categoria) updateParam('categoria', ''); // también quita la subcategoría
  };

  // Los filtros van al lado de los resultados en pantallas grandes y, en el celular, en la hoja que abre «Filtros»
  const panelFiltros = (
    <>
      {/* Ordenar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <SortOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="caption" fontWeight={700} color="text.secondary">ORDENAR POR</Typography>
      </Box>
      <RadioGroup value={orden} onChange={e => setOrden(e.target.value)}>
        <FormControlLabel value="rating" control={<Radio size="small" />} label={<Typography variant="body2">Mejores calificaciones</Typography>} />
        <FormControlLabel value="priceAsc" control={<Radio size="small" />} label={<Typography variant="body2">Menor precio</Typography>} />
        <FormControlLabel value="priceDesc" control={<Radio size="small" />} label={<Typography variant="body2">Mayor precio</Typography>} />
      </RadioGroup>
      <Divider sx={{ my: 2 }} />

      {/* Categoría */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <CategoryOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="caption" fontWeight={700} color="text.secondary">CATEGORÍA</Typography>
      </Box>
      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
        <Select displayEmpty value={categoria} onChange={e => updateParam('categoria', e.target.value)}
          sx={{ borderRadius: '8px' }}>
          <MenuItem value=""><em>Selecciona categoría</em></MenuItem>
          {(categories || []).map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl fullWidth size="small" disabled={!categoria}>
        <Select displayEmpty value={subcategoria} onChange={e => updateParam('subcategoria', e.target.value)}
          sx={{ borderRadius: '8px' }}>
          <MenuItem value=""><em>Selecciona subcategoría</em></MenuItem>
          {subcategories.map(s => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
        </Select>
      </FormControl>
      <Divider sx={{ my: 2 }} />

      {/* Tipo de búsqueda */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <SearchOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="caption" fontWeight={700} color="text.secondary">TIPO DE BÚSQUEDA</Typography>
      </Box>
      {[
        ['servicios', 'Servicios'],
        ['independientes', 'Páginas de servicios independientes'],
        ['grupos', 'Páginas de grupos y negocios'],
        ['personas', 'Personas'],
      ].map(([key, label]) => (
        <FormControlLabel key={key}
          control={<Checkbox size="small" checked={tipos[key]} onChange={e => setTipos(t => ({ ...t, [key]: e.target.checked }))} />}
          label={<Typography variant="body2">{label}</Typography>}
          sx={{ display: 'flex' }}
        />
      ))}
      <Divider sx={{ my: 2 }} />

      {/* Filtros avanzados */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
        onClick={() => setAdvOpen(o => !o)}>
        <Typography variant="subtitle2" fontWeight={700} color="primary.main">Filtros avanzados</Typography>
        <IconButton size="small"><ExpandMoreIcon sx={{ transform: advOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} /></IconButton>
      </Box>
      <Collapse in={advOpen}>
        <Box sx={{ px: 1, pt: 1 }}>
          <Typography variant="caption" color="text.secondary">Rango de precio (servicios)</Typography>
          <Slider value={rango} onChange={(e, v) => setPrecio(v)} min={0} max={tope} step={paso}
            disabled={data.servicios.length === 0}
            valueLabelDisplay="auto" valueLabelFormat={dinero} sx={{ color: 'primary.main', mt: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">{dinero(rango[0])}</Typography>
            <Typography variant="caption" color="text.secondary">{dinero(rango[1])}</Typography>
          </Box>
        </Box>
      </Collapse>
    </>
  );

  return (
    <Box className="main-page" sx={{ mb: 8 }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'secondary.main', color: 'primary.main', borderRadius: 2, px: 4, py: 5, mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Resultados de búsqueda</Typography>
        <Typography mt={2} variant="body1" color="primary">
          <small>
            {loading ? 'Buscando…' : `Encontramos ${totalVisible} resultado${totalVisible !== 1 ? 's' : ''}`}
            {q ? ` para "${q}"` : ''}
            {tag ? ` con la etiqueta` : ''}
          </small>
        </Typography>
        {tag && (
          <Box sx={{ mt: 1.5 }}>
            <Chip
              label={`#${tag}`}
              onDelete={() => updateParam('tag', '')}
              sx={{
                bgcolor: 'rgba(255,255,255,0.9)',
                color: 'primary.main',
                fontWeight: 700,
                '& .MuiChip-deleteIcon': { color: 'primary.main' }
              }}
            />
          </Box>
        )}
      </Box>

      <Grid container spacing={4}>
        {/* Sidebar de filtros (en el celular no: ahí van en la hoja de «Filtros») */}
        <Grid size={{ xs: 12, md: 3.5 }} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: 1, borderColor: 'sutil', position: { md: 'sticky' }, top: { md: 100 } }}>
            <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ mb: 2 }}>Filtrar búsqueda</Typography>
            {panelFiltros}
          </Paper>
        </Grid>

        {/* Contenido */}
        <Grid size={{ xs: 12, md: 8.5 }}>
          {/* En el celular, los filtros se abren con este botón (el número dice cuántos cambiaste) y el mapa, con el de al lado */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Button variant="outlined" startIcon={<TuneOutlinedIcon />} onClick={() => setFiltrosAbiertos(true)}
              aria-label={filtrosActivos > 0 ? `Filtros (${filtrosActivos} activo${filtrosActivos !== 1 ? 's' : ''})` : 'Filtros'}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', color: 'primary.main', borderColor: 'sutil', bgcolor: 'white' }}>
              Filtros
              {filtrosActivos > 0 && (
                <Box component="span" sx={{
                  ml: 1, minWidth: 20, height: 20, px: 0.75, borderRadius: '10px', bgcolor: 'secondary.main', color: 'primary.main',
                  fontSize: '0.75rem', fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {filtrosActivos}
                </Box>
              )}
            </Button>
            <Button variant="outlined" startIcon={<MapOutlinedIcon />} onClick={() => setMapaAbierto(a => !a)}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', color: 'primary.main', borderColor: 'sutil', bgcolor: 'white' }}>
              {mapaAbierto ? 'Ocultar mapa' : 'Ver mapa'}
            </Button>
          </Box>

          {/* Mapa con ubicaciones de los servicios encontrados */}
          {(pantallaGrande || mapaAbierto) && <SearchMap servicios={servicios} />}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: 'secondary.main' }} />
            </Box>
          ) : totalVisible === 0 ? (
            <Paper elevation={0} sx={{ p: 6, borderRadius: '16px', border: 1, borderColor: 'sutil', textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ mb: 1 }}>Sin resultados</Typography>
              <Typography variant="body2" color="text.secondary">
                No encontramos coincidencias{q ? ` para "${q}"` : ''}. Intenta con otra palabra o ajusta los filtros.
              </Typography>
            </Paper>
          ) : (
            <>
              {/* Primero servicios, páginas independientes y grupos; las personas van al final. */}
              {tipos.servicios && servicios.length > 0 && (
                <Section title="Servicios" subtitle="Servicios asociados a tu búsqueda" count={servicios.length} itemHeight={226}>
                  {servicios.map(s => <ServicioCard key={s._id} servicio={s} onVer={(x) => navigate(`/servicio/${x.userId}/${x._id}`)} />)}
                </Section>
              )}
              {tipos.independientes && independientes.length > 0 && (
                <Section title="Páginas de servicios independientes" subtitle="Profesionales con su propia página" count={independientes.length} itemHeight={178}>
                  {independientes.map(p => <PaginaCard key={p._id} pagina={p} onVer={(x) => navigate(`/pagina/${x._id}`)} />)}
                </Section>
              )}
              {tipos.grupos && grupos.length > 0 && (
                <Section title="Páginas de grupos y negocios" subtitle="Empresas y equipos especializados" count={grupos.length} itemHeight={178}>
                  {grupos.map(p => <PaginaCard key={p._id} pagina={p} onVer={(x) => navigate(`/pagina/${x._id}`)} />)}
                </Section>
              )}
              {tipos.personas && personas.length > 0 && (
                <Section title="Personas" subtitle="Proveedores que coinciden con tu búsqueda" count={personas.length} itemHeight={138}>
                  {personas.map(p => <PersonaCard key={p.userId} persona={p} onVer={(x) => navigate(`/perfil/${x.userId}`)} />)}
                </Section>
              )}
            </>
          )}
        </Grid>
      </Grid>

      {/* Hoja de filtros del celular (como la de la app) */}
      <Drawer anchor="bottom" open={filtrosAbiertos} onClose={() => setFiltrosAbiertos(false)}
        slotProps={{ paper: { sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '85vh' } } }}>
        <Box sx={{ px: 3, pt: 3 }}>
          <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ mb: 2 }}>Filtros</Typography>
          {panelFiltros}
        </Box>
        <Box sx={{
          position: 'sticky', bottom: 0, bgcolor: 'white', borderTop: 1, borderColor: 'sutil', px: 3, py: 2, mt: 2,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2
        }}>
          <Button onClick={limpiarFiltros} sx={{ textTransform: 'none', color: 'text.secondary' }}>Limpiar</Button>
          <Button variant="contained" disableElevation onClick={() => setFiltrosAbiertos(false)}
            sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>
            {loading ? 'Ver resultados' : `Ver ${totalVisible} resultado${totalVisible !== 1 ? 's' : ''}`}
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
};

export default SearchResults;
